import * as React from 'react';
import { useBloomreachPageRoutes } from '@grandvisionhq/bloomreach';
import { useGoogleMapsStaticMap } from '@grandvisionhq/common';
import { useLink } from '@grandvisionhq/frk-routing';
import { Spinner, Alert } from '@grandvisionhq/iris-ui';
import { useIntl, useDispatch } from '@grandvisionhq/state';
import { DateUtils } from '@grandvisionhq/utils';
import { rescheduleAppointment } from '../../../data-layer/appointments/actions.js';
import useVirtualAppointment from '../../hooks/useVirtualAppointment.js';
import { useGetAppointmentById } from '../../hooks/useGetAppointmentById.js';
import AppointmentDetail from './AppointmentDetail.js';

const AppointmentDetailContainer = ({ appointmentId  })=>{
    const { dateTime , getFormattedLabel , getLabel  } = useIntl();
    const dispatch = useDispatch();
    const { isVirtualAppointmentType  } = useVirtualAppointment();
    const [storeDetailModalOpen, setStoreDetailModalOpen] = React.useState(false);
    const storeButtonRef = /*#__PURE__*/ React.createRef();
    const { data: appointment , loading , error  } = useGetAppointmentById(appointmentId);
    const returnLabel = getLabel('appointments.rescheduling.returnLabel');
    const { routes  } = useBloomreachPageRoutes();
    const { href: appointmentsRoute  } = useLink({
        route: 'me.appointments'
    }) || {};
    const { href: appointmentsCancelRoute  } = useLink({
        route: 'appointments.cancel',
        params: {
            id: appointmentId,
            returnPath: appointmentsRoute,
            returnLabel
        }
    }) || {};
    const { href: appointmentsRescheduleRoute  } = useLink({
        route: 'appointments.reschedule',
        params: {
            id: appointmentId,
            returnPath: appointmentsRoute,
            returnLabel
        }
    }) || {};
    const rescheduleAppointmentRouteFromBloomreach = routes['appointment-reschedule'];
    const cancelAppointmentRouteFromBloomreach = routes['appointment-cancel'];
    const reschedulePath = appointmentsRescheduleRoute !== null && appointmentsRescheduleRoute !== void 0 ? appointmentsRescheduleRoute : rescheduleAppointmentRouteFromBloomreach ? `${rescheduleAppointmentRouteFromBloomreach}?id=${encodeURIComponent(appointmentId)}&returnPath=${appointmentsRoute}&returnLabel=${encodeURIComponent(returnLabel)}` : undefined;
    const cancelPath = appointmentsCancelRoute !== null && appointmentsCancelRoute !== void 0 ? appointmentsCancelRoute : cancelAppointmentRouteFromBloomreach ? `${cancelAppointmentRouteFromBloomreach}?id=${encodeURIComponent(appointmentId)}&returnPath=${appointmentsRoute}&returnLabel=${encodeURIComponent(returnLabel)}` : undefined;
    const dateAndTime = appointment ? `${dateTime.formatLongDate(appointment === null || appointment === void 0 ? void 0 : appointment.startTime)} - ${dateTime.formatTime(appointment === null || appointment === void 0 ? void 0 : appointment.startTime)}` : '';
    const store = appointment === null || appointment === void 0 ? void 0 : appointment.store;
    const mapImageUrl = useGoogleMapsStaticMap({
        lat: store === null || store === void 0 ? void 0 : store.lat,
        lon: store === null || store === void 0 ? void 0 : store.lon,
        params: '&style=feature:poi|visibility:off'
    });
    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent([
        store === null || store === void 0 ? void 0 : store.streetName.split(' '),
        store === null || store === void 0 ? void 0 : store.streetNumber,
        store === null || store === void 0 ? void 0 : store.town
    ].join(' ').trim())}`;
    const labels = {
        additionalInfo: getLabel('appointments.page.reschedule.confirm.additionalInformation'),
        addToCalendar: getLabel('appointments.confirmation.addToCalendar'),
        appointments: getLabel('account.menu.appointments'),
        breadcrumb: getLabel('account.appointment.detail.breadcrumb'),
        cancel: getLabel('account.appointment.cancel.title'),
        detailError: getLabel('account.appointment.detail.error'),
        getDirections: getLabel('appointments.confirmation.getDirections'),
        reschedule: getLabel('account.appointment.action.reschedule'),
        reservationInfo: getFormattedLabel('appointments.reservation.infoAlert.message', {
            parseHTML: true
        }),
        reservationTitle: getFormattedLabel('appointments.reservation.infoAlert.title', {
            parseHTML: true
        }),
        viewStoreDetails: getLabel('appointments.component.storeOverview.storeDetailLink'),
        when: getLabel('appointments.component.navigationContainer.step.when'),
        where: getLabel('appointments.component.navigationContainer.step.where'),
        who: getLabel('appointments.component.navigationContainer.step.who'),
        child: getLabel('appointments.component.appointmentOverview.child')
    };
    const handleOnReschedule = ()=>{
        appointment && dispatch(rescheduleAppointment(appointment));
    };
    const handleOpenStoreDetailModal = React.useCallback(()=>setStoreDetailModalOpen(true)
    , []);
    const handleCloseStoreDetailModal = React.useCallback(()=>{
        var ref;
        (ref = storeButtonRef.current) === null || ref === void 0 ? void 0 : ref.focus();
        setStoreDetailModalOpen(false);
    }, [
        storeButtonRef
    ]);
    if (loading) {
        return /*#__PURE__*/ React.createElement(Spinner, {
            "aria-label": getLabel('common.loadingText')
        });
    }
    if (error || !appointment) {
        return /*#__PURE__*/ React.createElement(Alert, {
            variant: "error",
            className: "alert-m-default"
        }, labels.detailError);
    }
    const isOldAppointment = DateUtils.isDateBefore(appointment.endTime, new Date());
    return /*#__PURE__*/ React.createElement(AppointmentDetail, {
        appointmentType: appointment.appointmentType,
        isVirtualAppointmentType: isVirtualAppointmentType(appointment.appointmentType),
        cancelLink: cancelPath,
        customerComment: appointment.customerComment,
        dateAndTime: dateAndTime,
        directionsUrl: !isVirtualAppointmentType(appointment.appointmentType) ? directionsUrl : undefined,
        endTime: appointment.endTime,
        invitee: appointment.invitee,
        isCancellable: appointment.isCancellable,
        isReschedulable: appointment.isReschedulable,
        isReservation: appointment.isReservation,
        labels: labels,
        mapImageUrl: !isVirtualAppointmentType(appointment.appointmentType) ? mapImageUrl : undefined,
        onReschedule: handleOnReschedule,
        onStoreDetailModalClose: handleCloseStoreDetailModal,
        onStoreDetailModalOpen: handleOpenStoreDetailModal,
        rescheduleLink: reschedulePath,
        startTime: appointment.startTime,
        storeDetailModalOpen: storeDetailModalOpen,
        store: !isVirtualAppointmentType(appointment.appointmentType) ? store : undefined,
        child: appointment.child,
        isOldAppointment: isOldAppointment
    });
};

export { AppointmentDetailContainer as default };
//# sourceMappingURL=AppointmentDetailContainer.js.map
